import { ParsedUrlQuery } from 'querystring';

import qs from 'qs';
import type { SearchState } from 'react-instantsearch-core';

export const DEBOUNCE_TIME = 700;

/**
 *
 * Takes in algolia search state and converts it into a url that can be used to load the same results. Good for
 * bookmarking, sharing etc.
 *
 */
export function createURL(state: SearchState): string {
  const isDefaultRoute =
    !state.query &&
    state.page === 1 &&
    !state.month &&
    !state.year &&
    !state.refinementList &&
    !state.refinementList?.country &&
    !state.refinementList?.['trip.activities'] &&
    !state.refinementList?.['trip.difficultyLevel'];

  if (isDefaultRoute) {
    return '';
  }
  const queryParameters = {
    ...(state.query && { query: encodeURIComponent(state.query) }),
    ...(state.page !== 1 && { page: state.page }),
    ...(state.refinementList?.country && {
      country: state.refinementList?.country?.join(','),
    }),
    ...(state.month && { month: state.month }),
    ...(state.year && { year: state.year }),
    ...(state.refinementList &&
      state.refinementList['trip.activities'] && {
        activities: state.refinementList['trip.activities'].join(','),
      }),
    ...(state.refinementList &&
      state.refinementList['trip.difficultyLevel'] && {
        difficultyLevel: state.refinementList['trip.difficultyLevel'].join(','),
      }),
  };

  const queryString = qs.stringify(queryParameters, {
    addQueryPrefix: true,
    arrayFormat: 'repeat',
  });

  return `/trips/${queryString}`;
}

export function createURLDepartures(state: SearchState): string {
  const isDefaultRoute =
    !state.query &&
    state.page === 1 &&
    !state.month &&
    !state.year &&
    !state.refinementList &&
    !state.refinementList?.country &&
    !state.refinementList?.['trip.activities'] &&
    !state.refinementList?.['trip.difficultyLevel'];

  if (isDefaultRoute) {
    return '';
  }
  const queryParameters = {
    ...(state.query && { query: encodeURIComponent(state.query) }),
    ...(state.page !== 1 && { page: state.page }),
    ...(state.refinementList?.country && {
      country: state.refinementList?.country?.join(','),
    }),
    ...(state.month && { month: state.month }),
    ...(state.year && { year: state.year }),
    ...(state.refinementList &&
      state.refinementList['trip.activities'] && {
        activities: state.refinementList['trip.activities'].join(','),
      }),
    ...(state.refinementList &&
      state.refinementList['trip.difficultyLevel'] && {
        difficultyLevel: state.refinementList['trip.difficultyLevel'].join(','),
      }),
  };

  const queryString = qs.stringify(queryParameters, {
    addQueryPrefix: true,
    arrayFormat: 'repeat',
  });

  return `/trips/departures/${queryString}`;
}

/**
 * Converts search state to url query params or returns null if not possible
 */
export function searchStateToUrl(searchState: SearchState): string {
  return searchState ? createURL(searchState) : '';
}

export function searchStateToUrlDepartures(searchState: SearchState): string {
  return searchState ? createURLDepartures(searchState) : '';
}

/**
 * Converts current url query params to algolia search state so that they can be used for filtering
 */
export function urlToSearchState(urlQuery?: ParsedUrlQuery): SearchState {
  if (!urlQuery) {
    return {};
  }
  //
  const {
    query = '',
    page = 1,
    country,
    month,
    year,
    activities,
    difficultyLevel,
  } = urlQuery;
  return {
    query: decodeURIComponent(query as string),
    page: page as number,
    refinementList: {
      ...(country && {
        country: (country as string).split(','),
      }),
      ...(activities && {
        'trip.activities': (activities as string).split(','),
      }),
      ...(difficultyLevel && {
        'trip.difficultyLevel': (difficultyLevel as string).split(','),
      }),
    },
    ...(month && { month }),
    ...(year && { year }),
  };
}
